/**
 * Deep-dive axis selection (ICCHP 2026 §6.4 (2)) — pure functions, no React.
 *
 * MIRRORS the backend (single source of truth for semantics):
 *   - CORE_AXES            ← CognitiveDifficultyScorer.CORE_AXES
 *                            (backend/app/services/ils_scorer.py; declaration
 *                            order doubles as the tie-break order)
 *   - CORE14_TYPE_MAP      ← CognitiveDifficultyScorer.TYPE_QUESTIONS_CORE14
 *                            (6 core axes only; modulators never trigger)
 *   - TRIGGER_THRESHOLD    ← CognitiveDifficultyScorer.TRIGGER_THRESHOLD
 *   - MAX_DEEP_DIVE_AXES   ← MAX_DEEP_DIVE_AXES (backend/app/routers/auth.py)
 *   - selectDeepDiveAxesFromScores
 *                          ← CognitiveDifficultyScorer.select_deep_dive_axes
 *
 * Severity definition (v2.16.16): an axis's severity IS its rounded core14
 * score (0-3 Likert average, round-half-up = Math.round on the non-negative
 * domain; HIGHER = more difficulty — 0 none / 1 mild / 2 moderate / 3 severe).
 * An axis is "triggered" when score >= TRIGGER_THRESHOLD; the trigger
 * threshold is uniform, so ordering by threshold exceedance descending is
 * identical to ordering by score descending.
 *
 * Selection contract (must stay byte-equivalent in behavior to the backend):
 *   1. keep triggered axes only (score >= TRIGGER_THRESHOLD);
 *   2. order by severity (score) DESCENDING;
 *   3. break ties by CORE_AXES declaration order (determinism);
 *   4. truncate to MAX_DEEP_DIVE_AXES.
 *
 * Register.js shows the deep-dive questions of the axes selected HERE. This
 * is the RECOMMENDED selection; the backend /api/auth/cognitive-deep-dive
 * contract is deliberately looser (SUBSET TOLERANCE, mixed-version safety):
 * it accepts any non-empty subset of the currently-triggered axes, at most
 * MAX_DEEP_DIVE_AXES distinct axes, each fully answered — so an old bundle's
 * declaration-order top-3 (or a pre-release Register tab) still completes.
 * Drift here therefore degrades WHICH axes get deep-dived (and still 400s if
 * an untriggered axis or a 4th axis is submitted) — keep it synced. CI sync
 * guard (static source parse — keep these exported literals as plain
 * literals):
 *   backend/tests/test_deep_dive_axis_selection_frontend_sync.py
 */

// Mirror of CognitiveDifficultyScorer.CORE_AXES (order = tie-break order).
export const CORE_AXES = [
  'working_memory',
  'visuospatial',
  'crossmodal',
  'load_vulnerability',
  'executive_inhibition',
  'language_semantic',
];

// Mirror of CognitiveDifficultyScorer.TYPE_QUESTIONS_CORE14 (core axes only).
export const CORE14_TYPE_MAP = {
  working_memory:       [45, 46],
  visuospatial:         [48, 49],
  crossmodal:           [51, 52],
  load_vulnerability:   [54, 55],
  executive_inhibition: [57, 58],
  language_semantic:    [60, 61],
};

// Mirror of CognitiveDifficultyScorer.TRIGGER_THRESHOLD.
export const TRIGGER_THRESHOLD = 2;

// Mirror of MAX_DEEP_DIVE_AXES (backend/app/routers/auth.py).
export const MAX_DEEP_DIVE_AXES = 3;

/**
 * Per-axis rounded scores from the core14 answers.
 *
 * Math.round is round-half-up over the non-negative Likert domain — matches
 * the backend's math.floor(x + 0.5) (ils_scorer.py score(), round-half-up
 * 統一). Axes with no answered item are OMITTED from the result (an
 * unanswered axis can never trigger).
 *
 * @param {Object} cogAnswers - {questionId: 0-3, ...}
 * @returns {Object} {axis: score(0-3)} for axes with >=1 answered item
 */
export function computeAxisScores(cogAnswers) {
  const scores = {};
  for (const axis of CORE_AXES) {
    const vals = CORE14_TYPE_MAP[axis]
      .map((id) => cogAnswers[id])
      .filter((v) => v !== undefined);
    if (vals.length === 0) continue;
    scores[axis] = Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }
  return scores;
}

/**
 * Triggered axes ordered by screening severity, capped at MAX_DEEP_DIVE_AXES.
 * Mirror of CognitiveDifficultyScorer.select_deep_dive_axes (backend).
 *
 * @param {Object} axisScores - {axis: score(0-3)}; missing/null count as 0
 * @returns {string[]} at most MAX_DEEP_DIVE_AXES axis names, severity-ordered
 */
export function selectDeepDiveAxesFromScores(axisScores) {
  const score = (axis) => axisScores[axis] || 0;
  return CORE_AXES
    .filter((axis) => score(axis) >= TRIGGER_THRESHOLD)
    // Explicit two-key comparator: severity descending, then declaration
    // index ascending (deterministic tie-break, independent of sort
    // stability guarantees).
    .sort((a, b) => (score(b) - score(a)) || (CORE_AXES.indexOf(a) - CORE_AXES.indexOf(b)))
    .slice(0, MAX_DEEP_DIVE_AXES);
}

/**
 * Composition used by Register.js at the Step 2 → deep-dive transition:
 * raw core14 answers → severity-ordered, capped deep-dive axes.
 *
 * @param {Object} cogAnswers - {questionId: 0-3, ...}
 * @returns {string[]} at most MAX_DEEP_DIVE_AXES axis names, severity-ordered
 */
export function selectDeepDiveAxes(cogAnswers) {
  return selectDeepDiveAxesFromScores(computeAxisScores(cogAnswers));
}
